import { useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { ProductModelInstance } from 'src/store/_models/product';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ProductOptionChoiceInstance } from 'src/store/_models/productOptionChoice';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import amplitude from 'src/_shared/amplitude';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';

/**
 * useGridItemContentHeaderLogic
 *
 * Handles hover state for the action icons in GridItemContentHeader.
 * The actual fade animations are handled by FadeIn/FadeOut in the component.
 */
export const useGridItemContentHeaderLogic = (product: ProductModelInstance) => {
  const {
    i18n,
    dynamicModal: { openModal },
    project: {
      goToMultiphotoEditorPage,
      selectUserImage,
      isProductEditModeActive,
      isProjectAddedToCart,
      activeProject,
    },
  } = useStore();

  const [productLookup, setProductLookup] = useState(LookupCodes.FORMAT);

  const isMultiphotoProductComplete =
    product.preview.isMultiphoto &&
    !isProductEditModeActive &&
    product.preview.userImageAreas.filter(e => e.userImage === undefined).length === 0;

  const onPressEdit = async (isMultiphoto: boolean) => {
    // for multiphoto product, there's another layer of editing
    if (isMultiphoto) {
      goToMultiphotoEditorPage();
    } else {
      const userImage = product.userImages[0];
      await selectUserImage(userImage);
    }
    openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.EditProducts,
    });
  };
  const [isLookupHovered, setIsLookupHovered] = useState(false);
  const [isEditHovered, setIsEditHovered] = useState(false);

  // Mouse enter/leave handlers for lookup icon
  const handleLookupMouseEnter = () => {
    setIsLookupHovered(true);
  };

  const handleLookupMouseLeave = () => {
    setIsLookupHovered(false);
  };

  // Mouse enter/leave handlers for edit icon
  const handleEditMouseEnter = () => {
    setIsEditHovered(true);
  };

  const handleEditMouseLeave = () => {
    setIsEditHovered(false);
  };

  const syncProductsOptions = () => {
    product.syncProductsOptions();
  };

  const getProductOptions = (type: LookupCodes) => {
    return product.lookupOptions?.find(item => item.code === type)?.choices;
  };

  const applyLookupToProductGroup = (item: ProductOptionChoiceInstance) => {
    activeProject?.applyLookupToProductGroup(
      [product],
      item.productConfigurationIdsMap,
      item.referencingOtherGroupCode,
    );

    let eventName, eventData;
    if (productLookup === LookupCodes.FORMAT) {
      eventName = AnalyticsEvents.personalize.editFormatClick;
      eventData = {
        format: item.productOptionChoiceCode,
        section_name: SECTION_NAMES.OPTIONS_MENU,
      };
    } else {
      eventName = AnalyticsEvents.personalize.photoPaperClick;
      eventData = {
        paper: item.productOptionChoiceCode,
      };
    }
    amplitude.trackEvent(eventName, eventData);
  };

  return {
    i18n,
    isLookupHovered,
    isEditHovered,
    handleLookupMouseEnter,
    handleLookupMouseLeave,
    handleEditMouseEnter,
    handleEditMouseLeave,
    onPressEdit,
    isMultiphotoProductComplete,
    isProjectAddedToCart,
    syncProductsOptions,
    getProductOptions,
    applyLookupToProductGroup,
    setProductLookup,
    productLookup,
  };
};
